import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { fetchActivityLogs, fetchBranches, fetchUsers, fetchItems, fetchSettings } from '@/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { pageTitle } from '@/lib/appConfig';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatInTimeZone } from 'date-fns-tz';

interface ActivityLog {
  id: string;
  user_id: string;
  branch_id: string;
  action: string;
  description: string;
  details: any | null;
  created_at: string;
}

interface User {
  id: string;
  name: string;
}

interface Branch {
  id: string;
  name: string;
}

interface Item {
  id: string;
  name: string;
  vat_enabled: number;
}

interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export default function ActivityLogs() {
  useEffect(() => {
    document.title = pageTitle('Activity Logs');
  }, []);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [vatPercentage, setVatPercentage] = useState<number>(0);
  const [systemTimezone, setSystemTimezone] = useState<string>('Africa/Windhoek');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
  });

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Access denied. Admins only.');
      setLoading(false);
      return;
    }

    async function loadData() {
      try {
        const [logsRes, usersRes, branchesRes, itemsRes, settingsRes] = await Promise.all([
          fetchActivityLogs(branchFilter === 'all' ? null : branchFilter, pagination.current_page),
          fetchUsers(),
          fetchBranches(),
          fetchItems(),
          fetchSettings(),
        ]);
        setLogs(logsRes.data);
        setPagination(logsRes.pagination);
        setUsers(usersRes.data);
        setBranches(branchesRes.data);
        setItems(itemsRes.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          vat_enabled: item.vat_enabled,
        })));
        setVatPercentage(settingsRes.data.vat_percentage || 0);
        setSystemTimezone(settingsRes.data.timezone || 'Africa/Windhoek');
        setLoading(false);
      } catch (err: any) {
        toast.error('Failed to load activity logs or branches');
        setLoading(false);
      }
    }
    loadData();
  }, [isAdmin, branchFilter, pagination.current_page]);

  const toggleRow = (id: string) => {
    setExpandedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const getUserName = (userId: string) => users.find(u => u.id === userId)?.name || 'Unknown';
  const getBranchName = (branchId: string) => branches.find(b => b.id === branchId)?.name || 'Unknown';
  const getItemName = (itemId: string) => items.find(i => i.id === itemId)?.name || 'Unknown Item';

  const getDetailsTitle = (action: string) => {
    if (action === 'inventory_transfer') return 'Transfer Details';
    if (action === 'delete_sale') return 'Sale Details';
    return 'Details';
  };

  const formatMoney = (value: number | string | null | undefined) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (num === null || num === undefined || Number.isNaN(num)) return '—';
    return `$${num.toFixed(2)}`;
  };

  const formatAction = (action: string) => {
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatTimestamp = (value: string) => {
    try {
      const parsed = new Date(`${value}Z`);
      if (Number.isNaN(parsed.getTime())) return value;
      return formatInTimeZone(parsed, systemTimezone, 'M/d/yyyy, h:mm:ss a');
    } catch {
      return value;
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      setPagination(prev => ({ ...prev, current_page: newPage }));
    }
  };

  if (!isAdmin) {
    return <div className="flex min-h-screen items-center justify-center text-red-500">Access denied. Admins only.</div>;
  }

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <header>
        <h1 className="text-3xl font-bold">Activity Logs</h1>
        <p className="text-muted-foreground">View actions performed by branch users</p>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Activity Log</CardTitle>
            <Select value={branchFilter} onValueChange={setBranchFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.length > 0 ? (
                  branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))
                ) : (
                  <SelectItem value="none" disabled>No branches available</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No activity logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map(log => (
                  <React.Fragment key={log.id}>
                    <TableRow>
                      <TableCell>{getUserName(log.user_id)}</TableCell>
                      <TableCell>{getBranchName(log.branch_id)}</TableCell>
                      <TableCell>{formatAction(log.action)}</TableCell>
                      <TableCell>{log.description}</TableCell>
                      <TableCell>
                        {log.details && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleRow(log.id)}
                          >
                            {expandedRows.includes(log.id) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            Details
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>{formatTimestamp(log.created_at)}</TableCell>
                    </TableRow>
                    {expandedRows.includes(log.id) && log.details && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <div className="p-4 bg-gray-50 rounded-md">
                            <h4 className="font-semibold mb-2">{getDetailsTitle(log.action)}</h4>
                            {log.action === 'inventory_transfer' ? (
                              <>
                                <p><strong>Item:</strong> {log.details.item_name || getItemName(log.details.item_id)}</p>
                                <p><strong>SKU:</strong> {log.details.item_sku || '—'}</p>
                                <p><strong>Quantity:</strong> {log.details.quantity ?? '—'}</p>
                                <p><strong>From Branch:</strong> {getBranchName(log.details.from_branch_id)}</p>
                                <p><strong>To Branch:</strong> {getBranchName(log.details.to_branch_id)}</p>
                              </>
                            ) : (
                              <>
                                <p><strong>Invoice #:</strong> {log.details.invoice_number || '—'}</p>
                                <p><strong>Total Amount:</strong> {formatMoney(log.details.total_amount)}</p>
                                {log.details.customer_name && (
                                  <p><strong>Customer Name:</strong> {log.details.customer_name}</p>
                                )}
                                {log.details.customer_phone && (
                                  <p><strong>Customer Phone:</strong> {log.details.customer_phone}</p>
                                )}
                                {log.details.customer_address && (
                                  <p><strong>Customer Address:</strong> {log.details.customer_address}</p>
                                )}
                                {Array.isArray(log.details.items) && log.details.items.length > 0 && (
                                  <>
                                    <h5 className="font-semibold mt-4 mb-2">Items</h5>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Item Name</TableHead>
                                          <TableHead>Quantity</TableHead>
                                          <TableHead>Price</TableHead>
                                          <TableHead>VAT</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {log.details.items.map((item: any, index: number) => {
                                          const itemRecord = items.find(i => i.id === item.item_id);
                                          const vatAmount = itemRecord?.vat_enabled
                                            ? item.quantity * item.price * (vatPercentage / 100)
                                            : 0;
                                          return (
                                            <TableRow key={index}>
                                              <TableCell>{getItemName(item.item_id)}</TableCell>
                                              <TableCell>{item.quantity}</TableCell>
                                              <TableCell>{formatMoney(item.price)}</TableCell>
                                              <TableCell>{vatAmount > 0 ? formatMoney(vatAmount) : '—'}</TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {logs.length} of {pagination.total} logs
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
